import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { formatDateAsUserTimezoneISO } from '../../utils/userTimezoneUtils';
import { useKanbanSchema } from '../../hooks/useKanbanSchema';
import { useAuth } from '../../contexts/AuthContext';

interface BacklogItem {
  id: string;
  title: string;
  description: string;
  priority: string; // Now dynamic from schema
  type: string; // Now dynamic from schema
  status: string;
  estimatedEffort: string;
  createdDate: string;
  created?: string; // Alternative field name used by API
  dependencies?: string;
  dueDate?: string;
  assignee?: string;
}

interface CardDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: BacklogItem | null;
  onSave: (card: BacklogItem) => void;
  columns: Array<{ id: string; title: string; color: string }>;
}

export const CardDetailsModal: React.FC<CardDetailsModalProps> = ({
  isOpen,
  onClose,
  card,
  onSave
}) => {
  const { user } = useAuth();
  const [editedCard, setEditedCard] = useState<BacklogItem | null>(null);
  const { priorities, types, loading } = useKanbanSchema();

  useEffect(() => {
    // Only update state when modal is open to avoid unnecessary resets
    if (!isOpen) return;
    
    if (card) {
      // Convert date to format suitable for HTML date input
      const formatDateForInput = (dateString: string): string => {
        if (!dateString) return '';
        
        // If already in YYYY-MM-DD format, use as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        
        // Otherwise, try to parse and format
        try {
          return formatDateAsUserTimezoneISO(dateString, user);
        } catch {
          return dateString;
        }
      };

      setEditedCard({ 
        ...card,
        createdDate: formatDateForInput(card.createdDate || card.created || ''),
        priority: card.priority || 'Medium',
        assignee: card.assignee || 'Chris' // Default to Chris if no assignee
      });
    } else {
      // Create a fresh new card template every time modal opens for new card creation
      setEditedCard({
        id: '', // Will be set when saving
        title: '',
        description: '',
        priority: 'Medium',
        type: 'Task',
        status: 'Not Started', // Default work status for new cards
        estimatedEffort: '',
        createdDate: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
        assignee: 'Chris'
      });
    }
  }, [card, isOpen]); // Added isOpen to dependency array

  if (!editedCard) return null;

  const handleSave = () => {
    if (editedCard) {
      onSave(editedCard);
    }
  };

  const updateField = (field: keyof BacklogItem, value: string) => {
    setEditedCard(prev => prev ? { ...prev, [field]: value } : null);
  };

  // Use dynamic options from schema (fallback to empty arrays during loading)
  const priorityOptions = priorities;
  const typeOptions = types;

  // Show loading state if schema is loading
  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Card Details" size="large">
        <div className="card-details">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading schema...
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Card Details" size="large">
      <div className="card-details">
        <div className="form-section">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={editedCard.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-main-layout">
            <div className="form-left">
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editedCard.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className="form-input form-textarea"
                  rows={8}
                />
              </div>
            </div>

            <div className="form-right">
              <div className="form-group">
                <label>Priority</label>
                <select
                  value={editedCard.priority}
                  onChange={(e) => updateField('priority', e.target.value)}
                  className="form-input"
                >
                  {priorityOptions.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  value={editedCard.type}
                  onChange={(e) => updateField('type', e.target.value)}
                  className="form-input"
                >
                  {typeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={editedCard.status}
                  onChange={(e) => updateField('status', e.target.value)}
                  className="form-input"
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Effort</label>
                <input
                  type="text"
                  value={editedCard.estimatedEffort}
                  onChange={(e) => updateField('estimatedEffort', e.target.value)}
                  className="form-input"
                  placeholder="e.g., 2 hours, 1 day"
                />
              </div>

              <div className="form-group">
                <label>Created</label>
                <input
                  type="date"
                  value={editedCard.createdDate}
                  onChange={(e) => updateField('createdDate', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Due</label>
                <input
                  type="date"
                  value={editedCard.dueDate || ''}
                  onChange={(e) => updateField('dueDate', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Assignee</label>
                <input
                  type="text"
                  value={editedCard.assignee || 'Chris'}
                  onChange={(e) => updateField('assignee', e.target.value)}
                  className="form-input"
                  placeholder="Assign to..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>

      <style>{`
        .card-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .form-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 2px;
        }

        .form-main-layout {
          display: grid;
          grid-template-columns: 1fr 312px;
          gap: 60px;
          align-items: start;
        }

        .form-left {
          display: flex;
          flex-direction: column;
        }

        .form-right {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .form-right .form-group {
          display: grid;
          grid-template-columns: 120px 180px;
          gap: 12px;
          align-items: center;
        }

        .form-right .form-group label {
          margin-bottom: 0;
          text-align: right;
          font-size: 13px;
        }

        .form-textarea {
          min-height: 350px;
          resize: vertical;
          max-width: 100%;
          max-height: 500px;
        }

        /* Style date input calendar icon for dark theme */
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
          cursor: pointer;
        }

        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          filter: invert(0.8);
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }

        @media (max-width: 768px) {
          .card-header-section {
            flex-direction: column;
            gap: 12px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </Modal>
  );
};